import React from 'react';
import { FilterBanner } from './FilterBanner';
import './cardStats.css';

/**
 * Shared grouped chip browser for category/sub-category selection. Used
 * by the Commander CardsTab (role → sub-category) and the deck panes
 * (parent category → subCategory). One group per top-level bucket; each
 * group contributes a clickable label (optional) and a row of chips.
 *
 * Selection shape: `{ kind: 'group' | 'chip', key }` or `null`.
 * Caller owns the state; this component is stateless.
 *
 * `groups` shape:
 *   [{
 *     key: 'consistency',
 *     label: 'Consistency',
 *     color: '#4a9bd6',               // optional accent color
 *     items: [{ key, label, count, title? }]
 *   }, ...]
 *
 * `allowGroupSelection` (default true) shows the label as a button;
 * pass false to render a plain text label (e.g. when the group itself
 * isn't a meaningful filter).
 */
export function CategoryChipBrowser({
    groups,
    selection,
    onSelectionChange,
    title,
    subtitle,
    allowGroupSelection = true,
    activeFilterLabel,
    activeFilterValue,
    activeFilterDetail,
    activeFilterColor,
    activeFilterHelp,
    onClearSelection,
}) {
    const safeGroups = Array.isArray(groups) ? groups : [];
    if (safeGroups.length === 0) return null;

    const toggle = (next) => {
        if (!onSelectionChange) return;
        if (selection && selection.kind === next.kind && selection.key === next.key) {
            onSelectionChange(null);
            return;
        }
        onSelectionChange(next);
    };

    return (
        <div className="statsSection">
            {(title || subtitle) && (
                <div className="statsSectionHeader">
                    {title && <span className="statsSectionTitle">{title}</span>}
                    {subtitle && <span className="statsSectionSubtitle">{subtitle}</span>}
                </div>
            )}
            <div className="roleChipGroups">
                {safeGroups.map((group) => {
                    const accent = group.color;
                    const isGroupActive =
                        selection?.kind === 'group' && selection?.key === group.key;
                    const showLabel = Boolean(group.label);
                    return (
                        <div key={group.key} className="roleChipGroup">
                            {showLabel && allowGroupSelection && (
                                <button
                                    type="button"
                                    className={`roleChipGroupLabel roleChipGroupLabelButton ${
                                        isGroupActive ? 'is-active' : ''
                                    }`}
                                    style={accent ? { color: accent } : undefined}
                                    onClick={() => toggle({ kind: 'group', key: group.key })}
                                    title={
                                        group.groupTitle ||
                                        `Show every item in ${group.label} combined`
                                    }
                                >
                                    {group.label}
                                </button>
                            )}
                            {showLabel && !allowGroupSelection && (
                                <span
                                    className="roleChipGroupLabel"
                                    style={accent ? { color: accent } : undefined}
                                >
                                    {group.label}
                                </span>
                            )}
                            {(group.items || []).length > 0 && (
                                <div className="roleChipRow">
                                    {group.items.map((item) => {
                                        const isActive =
                                            selection?.kind === 'chip' &&
                                            selection?.key === item.key;
                                        const chipStyle = accent
                                            ? isActive
                                                ? { borderLeftColor: accent, color: accent }
                                                : { borderLeftColor: accent }
                                            : undefined;
                                        return (
                                            <button
                                                key={item.key}
                                                type="button"
                                                className={`roleChip ${isActive ? 'is-active' : ''}`}
                                                onClick={() =>
                                                    toggle({ kind: 'chip', key: item.key })
                                                }
                                                style={chipStyle}
                                                title={item.title}
                                            >
                                                <span className="roleChipLabel">{item.label}</span>
                                                {item.count != null && (
                                                    <span className="roleChipCount">
                                                        {item.count}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {activeFilterValue && onClearSelection && (
                <FilterBanner
                    label={activeFilterLabel}
                    value={activeFilterValue}
                    detail={activeFilterDetail}
                    color={activeFilterColor}
                    onClear={onClearSelection}
                    ariaLabel="Clear selection"
                    help={activeFilterHelp}
                />
            )}
        </div>
    );
}
